import { DSDropdown } from "@components/design-system/molecules";
import { faUser } from "@fortawesome/pro-solid-svg-icons";
import { useAuth, useTeam } from "@hooks";
import { useDisplayName } from "@hooks/use-display-name";
import { getUserDisplayName } from "@src/utils";
import { useEffect } from "react";

function TeamFilterDropdown({
  isActive,
  userNoteCounts,
  currentTeam,
  selectedTeamMembers,
  setSelectedTeamMembers,
  refreshCurrentTeam,
  onDropdownOpenChange,
}) {
  const { userUuid, currentUserData } = useAuth();
  const { userTeams } = useTeam();

  useEffect(() => {
    if (isActive && refreshCurrentTeam) {
      refreshCurrentTeam();
    }
  }, [isActive, refreshCurrentTeam]);

  const handleMemberSelectChange = (value) => {
    if (value) {
      setSelectedTeamMembers([value]);
    } else {
      setSelectedTeamMembers([]);
    }
  };

  const handleAllTeamMembers = () => {
    setSelectedTeamMembers(
      currentTeam?.users?.map((user) => user.uuid) || [],
    );
  };

  const handleJustYou = () => {
    if (userUuid) {
      setSelectedTeamMembers([userUuid]);
    }
  };

  const displayValue =
    selectedTeamMembers?.length > 1
      ? ""
      : selectedTeamMembers?.[0] || currentUserData?.user?.uuid || "";

  if (!currentTeam?.users) {
    return null;
  }

  return (
    <div className="relative flex flex-col gap-sm">
      <DSDropdown
        options={currentTeam?.users || []}
        value={displayValue}
        onChange={handleMemberSelectChange}
        getOptionLabel={(user) => {
          return getUserDisplayName(user, userTeams, {
            includeLastName: true,
          });
        }}
        getOptionValue={(user) => user.uuid}
        placeholder="All"
        search
        searchPlaceholder="Search team..."
        width="w-52"
        leadingIcon={faUser}
        dataTestId="team-filter-select"
        onOpenChange={onDropdownOpenChange}
        getOptionTestId={(user) => `team-filter-option-${user.uuid}`}
        renderOption={(user) => (
          <TeamMemberOption
            user={user}
            userNoteCounts={userNoteCounts}
          />
        )}
      />
      <div className="flex flex-row items-center gap-sm">
        <button
          onClick={handleAllTeamMembers}
          className={`border border-brand rounded-full flex-1 py-1 text-sm text-center font-medium
            ${
              selectedTeamMembers?.length ===
              currentTeam?.users?.length
                ? "bg-brand text-white border-brand"
                : "text-brand hover:text-indigo-700 dark:hover:text-indigo-300"
            }`}
          data-testid="team-filter-all-members-button"
        >
          All Members
        </button>
        <button
          onClick={handleJustYou}
          className={`border border-brand rounded-full flex-1 py-1 text-sm text-center font-medium
            ${
              selectedTeamMembers?.length === 1 &&
              selectedTeamMembers[0] === userUuid
                ? "bg-brand text-white border-brand"
                : "text-brand hover:text-indigo-700 dark:hover:text-indigo-300"
            }`}
        >
          Just You
        </button>
      </div>
    </div>
  );
}

export default TeamFilterDropdown;

const TeamMemberOption = ({ user, userNoteCounts }) => {
  const displayName = useDisplayName(user, {
    includeLastName: true,
    youSuffix: true,
  });

  return (
    <span className="flex justify-between items-center w-full">
      <span>{displayName}</span>
      {userNoteCounts && (
        <span className="text-secondary ml-sm">
          ({userNoteCounts[user.uuid] || 0})
        </span>
      )}
    </span>
  );
};
